import { RemoteAdminData, RemoteData } from 'app/infrastructure/remote-data';
import { ApiResponse, Value } from 'app/types-shared';
import { endpoints } from './endpoints';
import { CodeListWire, LoadCodeItemsParams, MissingMappingWire, PostSearchResponseWire, PostSearchURLParams } from './types';
import { CodeMetadataWire, CodetablesAccessWire, ModifiedCodeMappingsModel } from './types-store';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';
import { LoadListParams } from 'app/area/code-mappings/components/types';

export const api = {

  getMissingMappings: async (id: number, params: LoadListParams): Promise<MissingMappingWire> => {
    const request = await new RemoteData(endpoints.searchCodeMappings(id))
      .resource<ApiResponse<MissingMappingWire>>()
      .filter(params)
      .single();

    return request.data;
  },

  getPostSearchValues: async (importSetId: number, searchParams: PostSearchURLParams): Promise<PostSearchResponseWire> => {
    const request: any = await new RemoteAdminData(endpoints.postSearchSLValues(importSetId))
      .resource<ApiResponse<PostSearchResponseWire>>()
      .create(searchParams, false);

    const json: ApiResponse<PostSearchResponseWire> = await request.json();

    return json.data;
  },

  saveImportSetMapping: async (importSetId: number, modifiedItems: ModifiedCodeMappingsModel[]) => {
    return new RemoteAdminData(endpoints.importSetMapping(importSetId))
      .resource<void>()
      .update(modifiedItems);
  },

  deleteImportSetMapping: (importSetId: number, codetableId: number, externalValues: string[]) => {
    return new RemoteAdminData(endpoints.importSetMapping(importSetId))
      .resource<void>()
      .filter({ importSetId })
      .httpDelete([{ codetableId, externalValues }]);
  },

  loadCodeItems: async (loadParams: LoadCodeItemsParams): Promise<CodeListWire> => {
    const {
      id, parentId, siblingId, categoryId, limitFieldsTo, code, query, includeDisabled,
      displayNotSelected, returnLeavesOnly, ignoreCategoryFilters
    } = loadParams;

    const response = await new RemoteAdminData(endpoints.optionsCollection(id)).resource<Response>()
      .filter({
        ...(parentId !== undefined && { parentId }),
        ...(siblingId !== undefined && { siblingId }),
        ...(displayNotSelected !== undefined && { displayNotSelected }),
        ...(returnLeavesOnly !== undefined && { returnLeavesOnly })
      })
      .create({
        includeInactiveCodes: {
          enabled: includeDisabled
        },
        ...(ignoreCategoryFilters && { ignoreCategoryFilters }),
        ...(limitFieldsTo && { limitFieldsTo }),
        ...(code && { code }),
        ...(!!query && {
          query: {
            text: query
          }
        }),
        ...(categoryId !== undefined && {
          category: {
            selectedIds: [
              {
                v: categoryId
              }
            ]
          }
        })
      });

    const json = await response.json();

    return json.data;
  },

  loadPath: async (codetableId: number, codeId: number): Promise<CodetableWireFormat> => {

    const response = await new RemoteAdminData(endpoints.optionPath(codetableId, codeId))
      .resource<ApiResponse<CodetableWireFormat>>()
      .single();

    return response.data;
  },

  loadCodetablesAccess: async (codetableId: number): Promise<CodetablesAccessWire> => {
    const request = await new RemoteAdminData(endpoints.codetablesAccess)
      .resource<ApiResponse<CodetablesAccessWire>>()
      .filter({ id: codetableId })
      .single();
    return request.data;
  },

  loadMetadata: async (id: number): Promise<CodeMetadataWire> => {
    const request = await new RemoteAdminData(endpoints.metadata(id))
      .resource<ApiResponse<CodeMetadataWire>>()
      .single();

    return request.data;
  },

  loadCategoryOptions: async (id: number, includeDisabledElements: boolean): Promise<Value[]> => {
    const request = await new RemoteAdminData(endpoints.categoryOptions(id))
      .resource<ApiResponse<Value[]>>()
      .filter({ includeDisabledElements })
      .all();

    return request.data;
  }
};
